import React, { useRef, useState } from 'react';
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteIcon from '@mui/icons-material/Delete';
import { Esercizio } from '../interfaces/Esercizio';
import { MODALTYPE, ModalData, modalDataClean, ModalAlert } from '../components/ModalAlert';
import { Chip, Box } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';


interface ModelViewerProps {
    title: string;
    description: string;
    acceptedFileType: string;
    file: string | null;
    field: keyof Esercizio;
    handleChange: <K extends keyof Esercizio>(key: K, value: Esercizio[K]) => void;
    fast?: boolean
    filename: string | null;
}

const JsonUpload: React.FC<ModelViewerProps> = ({
    title,
    description,
    acceptedFileType,
    file,
    field,
    handleChange,
    fast,
    filename,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [modalData, setModalData] = useState<ModalData>(modalDataClean);

    type Landmark = {
        x: number;
        y: number;
        z: number;
        presence: number;
        visibility: number;
        cameraAspectY: number;
    };

    type Frame = {
        landmarks: Landmark[];
        timerValue: number;
    };

    const isValidStructure = (data: unknown): data is Frame[] => {
      if (!Array.isArray(data)) return false;

      return data.every(frame =>
        typeof frame === "object" &&
        frame !== null &&
        Array.isArray((frame as any).landmarks) &&
        typeof (frame as any).timerValue === "number" &&
        (frame as any).landmarks.every(isValidLandmark)
      );
    }

    const isValidLandmark = (l: any): l is Landmark => {
      return (
        typeof l === "object" &&
        l !== null &&
        typeof l.x === "number" &&
        typeof l.y === "number" &&
        typeof l.z === "number" &&
        typeof l.presence === "number" &&
        typeof l.visibility === "number" &&
        typeof l.cameraAspectY === "number"
      );
    }

    const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const ext = selectedFile.name.split('.').pop()?.toLowerCase();
            if (ext === 'json') {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const text = event.target?.result as string;

                    try {
                        const json = JSON.parse(text);
                        if (!isValidStructure(json)) {
                            throw new Error("Struttura JSON non valida");
                        }
                    } catch (err) {
                        setModalData({
                            show: true,
                            title: "",
                            message: 'Json non valido',
                            type: MODALTYPE.OK,
                        })
                        return
                    }

                    handleChange(field, text as Esercizio[keyof Esercizio]);
                    handleChange("filename_json", selectedFile.name);
                };
                reader.readAsText(selectedFile);
            } else {
                setModalData({
                    show: true,
                    title: "Errore",
                    message: 'Formato non supportato: ' + ext,
                    type: MODALTYPE.OK,
                })
            }
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        handleChange(field, null);
        handleChange("filename_json", null);
    };

    const isValidUrl = (value: string): boolean => {
        try {
            JSON.parse(value);
            return false;
        } catch {
            return true;
        }
    }

  const handleDownload = async () => {
    try {
        if(file && filename){
            console.log(file)
            if(!isValidUrl(file)){
                setModalData({
                    show: true,
                    title: "",
                    message: 'File non salvato',
                    type: MODALTYPE.OK,
                })
                return
            }

          const response = await fetch(file, {
            method: "GET",
          });

          if (!response.ok) {
            throw new Error("Errore durante il download");
          }

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();

          a.remove();
          window.URL.revokeObjectURL(url);
        }
    } catch (err) {
      console.error(err);
    }
  }

    const closeModal = () => setModalData(prev => ({ ...prev, show: false }));


    return (
        <>
            <h5 className="text-lg font-semibold">{title}</h5>
            {fast ? (
                <>
                    <input
                        type="file"
                        accept={acceptedFileType}
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleChangeFile}
                    />
                    {!file && (

                        <button
                            className="btn flex gap-2 mb-4 btn-light"
                            style={{ backgroundColor: "#e7e7e7", color: "black" }}
                            onClick={handleButtonClick}>
                            <FileUploadIcon />
                            Carica file
                        </button>
                    )}
                    {file && (<>
                        <Chip
                            label={filename}
                            onDelete={() => handleRemoveFile()}
                            onClick={handleDownload}
                            deleteIcon={
                                <Box
                                    sx={{
                                        backgroundColor: 'black',
                                        borderRadius: '50%',
                                        padding: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <CloseIcon sx={{ color: 'white', fontSize: 18 }} />
                                </Box>
                            }
                            sx={{
                                cursor: 'default',
                                '&:hover': {
                                  cursor: 'pointer',
                                },
                                backgroundColor: 'black',
                                color: 'white',
                            }}

                        />
                    </>
                    )}
                </>
            ) : (
                <>
                    <p className="text-sm" style={{ color: "gray" }}>{description}</p>
                    <div className="text-center">
                        <input type="file" accept={acceptedFileType} ref={fileInputRef} style={{ display: "none" }} onChange={handleChangeFile} />
                        {file ? (
                            <>
                                <div className="border p-3 rounded-3 bg-light" >
                                    <div className="text-center m-1">
                                        <Chip
                                            label={filename}
                                            onDelete={() => handleRemoveFile()}
                                            onClick={handleDownload}
                                            deleteIcon={
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'black',
                                                        borderRadius: '50%',
                                                        padding: '2px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <CloseIcon sx={{ color: 'white', fontSize: 18 }} />
                                                </Box>
                                            }
                                            sx={{
                                                cursor: 'default',
                                                '&:hover': {
                                                  cursor: 'pointer',
                                                },
                                                backgroundColor: 'black',
                                                color: 'white',
                                            }}

                                        />
                                    </div>
                                </div>
                            </>
                        ) : (<>

                            <div className="border p-3 rounded-3 bg-light" >
                                <p>Trascina e rilascia <br />o</p>
                                <button className="btn flex items-center gap-2 mb-4" style={{ backgroundColor: "#1c492d", color: "white" }} onClick={handleButtonClick}>
                                    <FileUploadIcon />
                                    Carica file
                                </button>
                                <p className="text-sm" style={{ color: "gray" }}>Nessun file caricato</p>
                            </div>
                        </>
                        )}
                    </div>
                </>)}
            <ModalAlert
                modalData={modalData}
                onClose={closeModal} />
        </>
    );
}

export default JsonUpload;
